import { Accordion, Card } from '@mui/material';
import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import {
    BUDGET,
    BudgetExpenseOrIncome,
    BudgetSections,
} from 'business/constants';
import AtterrissageGrid from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/Components/AtterrissageGrid';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { useParams } from 'react-router-dom';
import PluriannualProjectionGrid from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/Components/PluriannualProjectionGrid';
import {
    ALL_PROJECTION_FILE_PREFIX,
    getExcelFilename,
} from 'routes/BudgetModule/contexts/helpers';
import ProjectionDafHeader from './ProjectionDafHeader';
import { useTranslation } from 'react-i18next';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import { getSectionLabel } from 'routes/BudgetModule/components/BudgetOverview/AllProjections/Components/helpers';

type ProjectionDafProps = {
    budgetProposal: BudgetProposalT;
    section: BudgetSections | null;
    expenseOrIncome: BudgetExpenseOrIncome;
    shouldShowOpenedGroups: boolean;
};

const StyAccordion = styled(Accordion, {
    name: 'StyAccordion',
})(({ theme }) => ({
    borderRadius: '4px',
    '&& .Mui-expanded': {
        minHeight: '0px',
        margin: '0',
    },
    '& .MuiIconButton-edgeEnd': {
        marginRight: 0,
    },
    '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
    },
    '&::before': {
        height: '0px',
    },
}));

const ProjectionDaf = ({
    budgetProposal,
    section,
    expenseOrIncome,
    shouldShowOpenedGroups,
}: ProjectionDafProps): React.ReactElement => {
    const { t } = useTranslation();
    const { projectionType = 'atterrissage' } = useParams<{
        projectionType: 'atterrissage' | 'pluriannual';
    }>();

    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const onChange = () => {
        setIsExpanded(!isExpanded);
        let trackEvent: string | null = null;
        if (projectionType === 'atterrissage') {
            trackEvent =
                TRACKABLE_EVENTS.budget.budgetOverview.projections.atterrissage
                    .sectionToggled;
        } else if (projectionType === 'pluriannual') {
            trackEvent =
                TRACKABLE_EVENTS.budget.budgetOverview.projections.pluriannual
                    .sectionToggled;
        }
        if (trackEvent !== null) {
            trackEventBudget(trackEvent, { section });
        }
    };

    if (budgetProposal === undefined) {
        return <></>;
    }

    return (
        <Card sx={{ width: '100%' }} elevation={1} key={section}>
            <StyAccordion defaultExpanded onChange={onChange}>
                <FullScreenProvider module={BUDGET}>
                    <ProjectionDafHeader
                        section={section}
                        isExpanded={isExpanded}
                    />
                    {projectionType === 'atterrissage' ? (
                        <AtterrissageGrid
                            budgetProposal={budgetProposal}
                            isDaf
                            expenseOrIncome={expenseOrIncome}
                            excelExport={{
                                fileName: getExcelFilename(
                                    ALL_PROJECTION_FILE_PREFIX,
                                    [expenseOrIncome, projectionType],
                                ),
                                sheetName: getSectionLabel(
                                    budgetProposal.section,
                                    t,
                                ),
                            }}
                            shouldShowOpenedGroups={shouldShowOpenedGroups}
                        />
                    ) : (
                        <PluriannualProjectionGrid
                            isDaf
                            budgetProposal={budgetProposal}
                            expenseOrIncome={expenseOrIncome}
                            excelExport={{
                                fileName: getExcelFilename(
                                    ALL_PROJECTION_FILE_PREFIX,
                                    [expenseOrIncome, projectionType],
                                ),
                                sheetName: getSectionLabel(
                                    budgetProposal.section,
                                    t,
                                ),
                            }}
                            shouldShowOpenedGroups={shouldShowOpenedGroups}
                        />
                    )}
                </FullScreenProvider>
            </StyAccordion>
        </Card>
    );
};

export default ProjectionDaf;
